import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useData } from "../context/DataContext";
import HeroSlider from "../components/HeroSlider";
import ApplyVideos from "../components/ApplyVideos";
import { processSteps } from "../mock/mock";
import { api, mediaUrl } from "../api";
import useSeo from "../hooks/useSeo";

const Home = () => {
  const { tr, ui } = useLang();
  const { categories } = useData();
  const [projects, setProjects] = useState([]);
  useSeo({
    title: { en: "Mosaic Art — Handcrafted in Antioch", tr: "Mozaik Sanatı — Antakya'da El Yapımı" },
    description: { en: "HAAS Mosaic creates handcrafted marble mosaics — portraits, mythology, 3D, wall, floor and pool art — inspired by the ancient heritage of Antioch.", tr: "HAAS Mosaic; Antakya'nın antik mirasından ilham alan el yapımı mermer mozaikler üretir — portre, mitoloji, 3D, duvar, zemin ve havuz sanatı." },
  });

  useEffect(() => {
    api.projects().then(setProjects).catch(() => {});
  }, []);

  return (
    <div>
      <HeroSlider />

      {/* Intro / Where Art Meets Mastery */}
      <section className="py-24 md:py-32 bg-[#f7f3ee]">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-14 items-center">
          <div className="card-img-zoom overflow-hidden">
            <img src="/mosaic/2025/08/5E13C82F-B535-4056-8474-CA7AA1B9AB69-1413x2048.jpeg" alt="HAAS Mosaic usta zanaatkâr mozaik işçiliği / master artisan crafting a mosaic" className="w-full h-[540px] object-cover" />
          </div>
          <div>
            <p className="text-[#a67c52] text-[12px] uppercase tracking-luxe mb-4">{ui.welcome}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] mb-6 text-[#211d18]">{ui.whereArt}</h2>
            <p className="text-[#5a5348] text-[16px] leading-relaxed mb-8 max-w-lg">{ui.whereArtText}</p>
            <Link to="/shop" className="group inline-flex items-center gap-3 text-[13px] uppercase tracking-[0.16em] text-[#211d18] border-b-2 border-[#a67c52] pb-1.5 hover:text-[#a67c52] transition-colors">
              {ui.letEnchant} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Choose Your Style — collections grid */}
      <section className="pb-24 md:pb-32 bg-[#f7f3ee]">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#a67c52] text-[12px] uppercase tracking-luxe mb-3">HAAS MOSAIC</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#211d18]">{ui.chooseStyle}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.map((c) => (
              <Link key={c.slug} to={`/product-category/${c.slug}`} className="card-img-zoom group relative overflow-hidden h-[300px] md:h-[380px] bg-[#211d18]">
                <img src={mediaUrl(c.image)} alt={tr(c.name)} loading="lazy" decoding="async" style={{ objectPosition: c.slug === "portrate" ? "22% center" : "center" }} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <p className="text-[#e2c9a8] text-[10px] uppercase tracking-[0.24em] mb-2">HAAS MOSAIC</p>
                  <h3 className="text-white font-serif text-2xl md:text-3xl mb-1">{tr(c.name)}</h3>
                  <span className="flex items-center gap-1.5 text-white/80 text-[12px] uppercase tracking-[0.14em] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                    {ui.discover} <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How We Do — process strip */}
      <section className="py-24 md:py-32 bg-[#211d18] text-white">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-[#e2c9a8] text-[12px] uppercase tracking-luxe mb-4">HAAS MOSAIC</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-5">{ui.howWeDo}</h2>
            <p className="text-white/70 text-[16px] leading-relaxed">{ui.processIntro}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {processSteps.slice(0, 4).map((s, idx) => (
              <div key={idx} className="group">
                <div className="card-img-zoom overflow-hidden mb-4 h-52">
                  <img src={s.image} alt={tr(s.title)} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <span className="text-[#a67c52] font-serif text-2xl">0{idx + 1}</span>
                <h3 className="font-serif text-xl mt-1">{tr(s.title)}</h3>
                <p className="text-white/55 text-[13px] mt-1">{tr(s.subtitle)}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/how-we-do" className="group inline-flex items-center gap-3 border border-white/40 text-white text-[13px] uppercase tracking-[0.16em] px-8 py-4 hover:bg-white hover:text-[#211d18] transition-colors">
              {ui.viewAll} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* How it's applied — videos */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 bg-[#f7f3ee]" data-testid="home-apply-section">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-[#a67c52] text-[12px] uppercase tracking-luxe mb-4">HAAS MOSAIC</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-5 text-[#211d18]">{ui.applyTitle}</h2>
            <p className="text-[#5a5348] text-[16px] leading-relaxed">{ui.applyIntro}</p>
          </div>
          <ApplyVideos compact />
        </div>
      </section>

      {/* Projects preview */}
      <section className="pt-10 md:pt-12 pb-24 md:pb-32 bg-[#f7f3ee]">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div className="max-w-xl">
              <p className="text-[#a67c52] text-[12px] uppercase tracking-luxe mb-3">{ui.nav.projects}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#211d18] mb-4">{ui.allProjects}</h2>
              <p className="text-[#5a5348] text-[16px] leading-relaxed">{ui.projectsIntro}</p>
            </div>
            <Link to="/all-projects" className="group inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.16em] text-[#211d18] hover:text-[#a67c52] transition-colors whitespace-nowrap">
              {ui.viewAll} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {projects.slice(0, 3).map((p) => (
              <div key={p.id} className="card-img-zoom group relative overflow-hidden h-[420px]">
                <img src={mediaUrl(p.image)} alt={tr(p.name)} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
                <div className="absolute bottom-0 p-7">
                  <p className="text-[#e2c9a8] text-[11px] uppercase tracking-[0.2em] mb-2">{tr(p.location)}</p>
                  <h3 className="text-white font-serif text-2xl">{tr(p.name)}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden">
        <img src="/mosaic/2025/08/tavus.jpeg" alt="HAAS Mosaic el yapımı tavus kuşu mermer mozaik / handcrafted peacock marble mosaic" loading="lazy" decoding="async" style={{ objectPosition: "center 25%" }} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-serif text-4xl md:text-6xl text-white mb-6">{ui.ctaTitle}</h2>
          <p className="text-white/85 text-[17px] leading-relaxed mb-9 max-w-xl mx-auto">{ui.ctaText}</p>
          <Link to="/contact" className="inline-flex items-center gap-3 bg-[#a67c52] hover:bg-[#8f6844] text-white text-[13px] uppercase tracking-[0.16em] px-9 py-4 transition-colors">
            {ui.getInTouch} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
